// web/src/components/Layout.tsx
// Application shell — sticky header (wordmark + plan badge + sign-out),
// outlet for the page, and a footer with legal links. Restyled to the new
// design language: Anton-italic wordmark in the header, no boxy "M" badge.

import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { LANDING_URL, PRIVACY_URL, TERMS_URL } from '../config';
import { CookieBanner } from './CookieBanner';

const brandLinkStyle = { textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 } as const;

const brandMark = (
  <>
    <img
      src="/apple-touch-icon.png"
      alt=""
      width={36}
      height={36}
      style={{ display: 'block', width: 36, height: 36, borderRadius: 8 }}
    />
    <span
      className="mfj-brand-text"
      style={{
        fontFamily: 'var(--font-masthead)',
        fontStyle: 'italic',
        fontSize: '1.25rem',
        color: 'var(--green)',
        textTransform: 'uppercase',
        letterSpacing: 0,
      }}
    >
      MyFootballJournal
    </span>
  </>
);

export function Layout() {
  const { user, signOut } = useAuth();

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px',
          background: 'rgba(250, 245, 236, 0.85)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderBottom: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Logged-in → dashboard (in-app). Logged-out → the marketing site,
            not the SPA's own Welcome route — a visitor mid-signup who taps the
            logo expects myfootballjournal.com. */}
        {user ? (
          <Link to="/dashboard" style={brandLinkStyle} aria-label="MyFootballJournal home">
            {brandMark}
          </Link>
        ) : (
          <a href={LANDING_URL} style={brandLinkStyle} aria-label="MyFootballJournal home">
            {brandMark}
          </a>
        )}

        {user ? (
          <nav className="mfj-header-nav" style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 14 }}>
            <Link
              to="/upgrade"
              className="mfj-upgrade-link"
              style={{
                color: 'var(--green)',
                textDecoration: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--small)',
              }}
            >
              Upgrade
            </Link>
            <Link
              to="/settings"
              style={{
                color: 'var(--muted)',
                textDecoration: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--small)',
              }}
            >
              Settings
            </Link>
            <button
              onClick={() => void signOut()}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--muted)',
                fontSize: 14,
                cursor: 'pointer',
                padding: '6px 10px',
                fontFamily: 'inherit',
              }}
            >
              Sign out
            </button>
          </nav>
        ) : null}
      </header>

      <main style={{ flex: 1, padding: '32px 24px' }}>
        <div style={{ maxWidth: 'var(--col-max)', margin: '0 auto', width: '100%' }}>
          <Outlet />
        </div>
      </main>

      <footer
        style={{
          padding: '24px',
          textAlign: 'center',
          fontSize: 13,
          color: 'var(--muted)',
          borderTop: '1px solid var(--divider)',
          marginTop: 'var(--space-xl)',
        }}
      >
        <a href={PRIVACY_URL} style={{ color: 'inherit', marginRight: 16 }}>Privacy</a>
        <a href={TERMS_URL} style={{ color: 'inherit' }}>Terms</a>
        <div style={{ marginTop: 8, fontFamily: 'var(--font-body)', fontSize: 'var(--micro)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          © 2026 The Football journal
        </div>
      </footer>

      {/* Cookie consent banner — renders only when no decision is stored. */}
      <CookieBanner />
    </div>
  );
}
